/**
 * Base API Configuration
 * Centralized axios instance with common configuration
 */
import axios from 'axios';
import storageService from './platform/storageService';

// Get backend URL from environment
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const NORMALIZED_BACKEND_URL = BACKEND_URL.replace(/\/api\/?$/, '');
const API_SUFFIX_PATTERN = /\/api\/?$/;

// Create axios instance with default config
const api = axios.create({
  baseURL: `${NORMALIZED_BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token to all requests
api.interceptors.request.use(
  (config) => {
    // storageService.getItemSync falls back to localStorage on web and on native
    // until the async Preferences plugin resolves (token is also mirrored there).
    const token = storageService.getItemSync('edsupp_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle common errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Only trigger logout when the explicit session-check endpoint (/auth/me) returns
    // 401.  Relying on arbitrary endpoint 401s caused false logouts: a failing
    // worksheet/notes generation (or any concurrent background request) could
    // return 401 and silently boot the user mid-session.
    //
    // All other 401s are surfaced to the calling component as a rejected promise
    // so they can show an appropriate error message (e.g. "generation failed,
    // please try again") without destroying the session.
    const url = error.config?.url || '';
    const isAuthCheck = url.includes('/auth/me');

    // Login/register endpoints: never trigger logout (they show inline form errors).
    const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/register');

    if (error.response?.status === 401 && !isAuthEndpoint) {
      // Broadcast a soft signal for non-auth-check 401s so components can react
      // (e.g. show a "your session may have expired" toast and trigger a
      // /auth/me re-check) without us forcibly logging out and losing in-flight work.
      if (!isAuthCheck) {
        window.dispatchEvent(
          new CustomEvent('auth:session-warning', { detail: { url } })
        );
      } else {
        await storageService.removeItem('edsupp_token');
        await storageService.removeItem('user');
        // Signal React Router to navigate rather than hard-reloading the WebView.
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
      }
    }
    return Promise.reject(error);
  }
);

export const getApiBaseUrl = () => api.defaults.baseURL;

export const setApiBaseUrl = (baseUrl) => {
  api.defaults.baseURL = baseUrl;
};

export const getFallbackBaseUrl = () => {
  if (api.defaults.baseURL.match(API_SUFFIX_PATTERN)) {
    return api.defaults.baseURL.replace(API_SUFFIX_PATTERN, '');
  }
  return api.defaults.baseURL;
};

export default api;
